/**
 * Create Or Edit Table Tool
 * 
 * Create a new table or edit an existing table on a slide.
 * Uses rowOperations and columnOperations for structural changes.
 * Cell text uses paragraphOperations[].
 * 
 * Primary: Office.js TypeScript implementation
 * Fallback: Python backend COM service at /com/createTable
 */

import { ToolDescription, safeParseToolParams } from "../../../base/toolDescription";
import { ToolBase } from "../../../base/toolBase";
import {
    CreateOrEditTableParamsSchema,
    type CreateOrEditTableParams,
    type RowOperation,
    type ColumnOperation,
    type CellTextWithOperations,
    type CopyStyleFrom,
} from "./createOrEditTable.schema";
import { getOrCreatedocumentId } from "../../../../../services/documentIdManager";
import { BACKEND_BASE_URL } from "../../../../../services/communication/backendClient";
import { applyShapeMetadata } from "../../../../../services/metadataService";

// ═══════════════════════════════════════════════════════════════════════════
// TYPE EXTENSIONS FOR OFFICE.JS (PowerPointApi 1.8+)
// See: https://learn.microsoft.com/en-us/javascript/api/powerpoint/powerpoint.table
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Extended TableCell interface - cells have a direct 'text' property
 * API: PowerPointApi 1.8
 */
interface ExtendedTableCell extends PowerPoint.TableCell {
    text: string;  // Direct text property per API docs
    readonly fill: PowerPoint.ShapeFill;
}

/**
 * Extended Table interface with methods from PowerPointApi 1.8+
 * Using getCellOrNullObject as per the official API
 * Note: deleteRow/insertRow/deleteColumn/insertColumn may not be in all API versions
 */
interface ExtendedTable extends PowerPoint.Table {
    getCellOrNullObject(rowIndex: number, columnIndex: number): ExtendedTableCell;
    mergeCells(rowIndex: number, columnIndex: number, rowCount: number, columnCount: number): void;
    readonly rows: PowerPoint.TableRowCollection;
    readonly columns: PowerPoint.TableColumnCollection;
    // Row/column manipulation methods (may require newer API versions)
    deleteRow(rowIndex: number): void;
    insertRow(rowIndex: number, count?: number): void;
    deleteColumn(columnIndex: number): void;
    insertColumn(columnIndex: number, count?: number): void;
}

/**
 * Extended Shape interface with getTable() method
 * API: PowerPointApi 1.8
 */
interface ShapeWithGetTable extends PowerPoint.Shape {
    getTable(): ExtendedTable;
}

/**
 * Get the table from a shape using the correct API method: shape.getTable()
 * See: https://learn.microsoft.com/en-us/javascript/api/powerpoint/powerpoint.table
 */
function getTableFromShape(shape: PowerPoint.Shape): ExtendedTable {
    return (shape as ShapeWithGetTable).getTable();
}

// ═══════════════════════════════════════════════════════════════════════════
// RESPONSE TYPES
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Create or edit table result
 */
export interface CreateOrEditTableResult {
    success: boolean;
    slideId: number;
    shapeId: number;
    operationType: "create" | "edit";
    dimensions?: {
        rows: number;
        columns: number;
    };
    position?: {
        left: number;
        top: number;
        width: number;
        height: number;
    };
    warnings: string[];
    errors: string[];
    executionMethod?: "officejs" | "com";
}

// ═══════════════════════════════════════════════════════════════════════════
// CREATE OR EDIT TABLE TOOL
// ═══════════════════════════════════════════════════════════════════════════

// System prompt fallback for Keywords AI
const TABLE_SYSTEM_PROMPT = `### createOrEditTable
Create a new table or edit an existing table on a slide.

**When to use:**
- Creating data tables with rows and columns
- Editing table content or structure
- Formatting table cells

**CRITICAL (create):** Provide cellText for EVERY cell. For an R×C table, provide R×C cell entries.
**Edit:** Provide only changed cells unless you are applying formatting across a full row/column.

**Features:**
- Create tables with specified dimensions
- Row/column operations (insert/remove)
- Cell text with paragraphOperations
- Cell formatting (fill, borders)
 - Defaults: header row/col center+middle, other cells left+middle; text black, fill/borders blank

\`\`\`json
{
  "slideId": 1,
  "operationType": "create",
  "position": { "left": 50, "top": 100, "width": 400, "height": 200 },
  "tableConfig": {
    "rows": 2,
    "columns": 2,
    "cellText": [
      { "cell": { "row": 1, "col": 1 }, "textContent": { "paragraphOperations": [{ "operation": "insert", "index": 0, "paragraph": { "setText": "A", "textFormat": [{ "text": "A" }] } }] } },
      { "cell": { "row": 1, "col": 2 }, "textContent": { "paragraphOperations": [{ "operation": "insert", "index": 0, "paragraph": { "setText": "B", "textFormat": [{ "text": "B" }] } }] } },
      { "cell": { "row": 2, "col": 1 }, "textContent": { "paragraphOperations": [{ "operation": "insert", "index": 0, "paragraph": { "setText": "C", "textFormat": [{ "text": "C" }] } }] } },
      { "cell": { "row": 2, "col": 2 }, "textContent": { "paragraphOperations": [{ "operation": "insert", "index": 0, "paragraph": { "setText": "D", "textFormat": [{ "text": "D" }] } }] } }
    ]
  }
}
\`\`\``;

/**
 * Tool for creating or editing tables on slides.
 * 
 * Features:
 * - Create new tables with specified rows/columns
 * - Edit existing tables by ID
 * - Row operations (insert/remove) with spacing consequences
 * - Column operations (insert/remove) with spacing consequences
 * - Per-cell text with paragraphOperations[]
 * - Cell formatting (fill, borders)
 * - Cell merging
 * - Metadata support in edit mode
 * 
 * Note: Tables don't have shape-level fill/outline/shadow -
 * those are handled by individual cells.
 */
export class CreateOrEditTableTool extends ToolBase<CreateOrEditTableParams, CreateOrEditTableResult> {
    public readonly name = "createOrEditTable";
    public readonly description: ToolDescription;
    
    // System prompt description
    public readonly systemPromptDescription = TABLE_SYSTEM_PROMPT;
    
    // Streaming operation fields
    fieldsToCheck = [
        { name: 'slideId', type: 'number' as const, list_status: false, order: 1 },
        { name: 'operationType', type: 'string' as const, list_status: false, order: 2 },
        { name: 'shapeId', type: 'number' as const, list_status: false, order: 3 },
    ];
    
    listsToCheck = [];

    constructor() {
        super();
        this.description = new ToolDescription(
            "createOrEditTable",
            `Create or edit a table on a slide.

**CRITICAL (create): You MUST provide cellText for EVERY cell in the table. Do NOT leave cells empty.**
**Edit:** Provide only changed cells unless applying formatting across an entire row/column.

**REQUIRED STRUCTURE:**
- slideId: number (1-based)
- operationType: "create" | "edit"
- tableConfig: { rows: number, columns: number, cellText: [...] } (REQUIRED - include ALL cells!)
- For edit: shapeId is required

**IMPORTANT:** For a table with R rows and C columns, you must provide R×C cell entries.
Example: 4 rows × 3 columns = 12 cellText entries (one for each cell).

**AUTO-RESIZE BEHAVIOR:**
When adding rows or columns to an existing table:
- autoResize: true (DEFAULT) - Table maintains its original size. Row heights and column widths are automatically redistributed proportionally to fit the new rows/columns within the existing bounds. Use this to prevent tables from overflowing their allocated space.
- autoResize: false - Table expands to accommodate new rows/columns at their default sizes. Use this when you have room for the table to grow.

Minimum dimensions enforced: 18pt row height, 30pt column width.

**ROW/COLUMN OPERATIONS (edit mode):**
- rowOperations: [{ operation: "insert" | "remove", index: number, insertPosition?: "at" | "after", count?: number }]
- columnOperations: [{ operation: "insert" | "remove", index: number, insertPosition?: "at" | "after", count?: number }]
- For insert: index is 1-based and refers to the anchor row/column.
- For insert: insertPosition is REQUIRED ("at" = before the anchor, "after" = after the anchor).
- For remove: index is which row/column to delete.

**COPY STYLE FROM ANOTHER TABLE:**
Use \`tableConfig.copyStyleFrom\` to apply styling from a source table:
- Detects patterns: header row, header column, alternating rows (zebra striping)
- Copies: colors, fonts, borders, alignments, dimensions
- Optional adjustments: scale dimensions, remap colors
**Theme consistency:** Prefer matching the presentation theme and nearby tables. Use copyStyleFrom or inherit existing table patterns before inventing new styling.
**Defaults:** Cell fills and borders are blank; text is black unless styled.
**Alignment guidance:** Header rows/columns should be middle+center. Short labels/dates are usually middle+center. Long multiline text should be middle+left. Numbers are usually middle+center or middle+right (55/35), occasionally middle+left (10). If the header row is tall, bottom alignment can read better.
**Default alignment (if you don't set textFrame):** only the (row 1, col 1) cell is centered; all other cells default to middle+left.

**Cell text format (tight):**
{ "cell": { "row": 1, "col": 1 }, "textContent": { "paragraphOperations": [{ "operation": "insert", "index": 0, "paragraph": { "setText": "Header", "textFormat": [{ "text": "Header", "bold": true }] } }] } }

**For editing text formatting (colors, fonts, etc.) in existing cells:**
- Use "operation": "edit" with textFormat in the paragraph object
- Each textFormat run describes the text to format and its styling (color, bold, fontSize, etc.)
- **CRITICAL:** When updating formatting for multiple cells (e.g., all cells in a column), you MUST provide cellText entries for EACH cell with the formatting changes
- Example: Update text color to red for all cells in column 2:
  "cellText": [
    { "cell": { "row": 1, "col": 2 }, "textContent": { "paragraphOperations": [{ "operation": "edit", "index": 0, "paragraph": { "setText": "Q1", "textFormat": [{ "text": "Q1", "textColor": "#FF0000" }] } }] } },
    { "cell": { "row": 2, "col": 2 }, "textContent": { "paragraphOperations": [{ "operation": "edit", "index": 0, "paragraph": { "setText": "Q2", "textFormat": [{ "text": "Q2", "textColor": "#FF0000" }] } }] } },
    { "cell": { "row": 3, "col": 2 }, "textContent": { "paragraphOperations": [{ "operation": "edit", "index": 0, "paragraph": { "setText": "Q3", "textFormat": [{ "text": "Q3", "textColor": "#FF0000" }] } }] } }
  ]

**Complete Example (2x3, all 6 cells):**
{
  "slideId": 1,
  "operationType": "create",
  "position": { "left": 50, "top": 100, "width": 600, "height": 150 },
  "tableConfig": {
    "rows": 2,
    "columns": 3,
    "cellText": [
      { "cell": { "row": 1, "col": 1 }, "textContent": { "paragraphOperations": [{ "operation": "insert", "index": 0, "paragraph": { "setText": "Name", "textFormat": [{ "text": "Name", "bold": true }] } }] } },
      { "cell": { "row": 1, "col": 2 }, "textContent": { "paragraphOperations": [{ "operation": "insert", "index": 0, "paragraph": { "setText": "Value", "textFormat": [{ "text": "Value", "bold": true }] } }] } },
      { "cell": { "row": 1, "col": 3 }, "textContent": { "paragraphOperations": [{ "operation": "insert", "index": 0, "paragraph": { "setText": "Notes", "textFormat": [{ "text": "Notes", "bold": true }] } }] } },
      { "cell": { "row": 2, "col": 1 }, "textContent": { "paragraphOperations": [{ "operation": "insert", "index": 0, "paragraph": { "setText": "Item A", "textFormat": [{ "text": "Item A" }] } }] } },
      { "cell": { "row": 2, "col": 2 }, "textContent": { "paragraphOperations": [{ "operation": "insert", "index": 0, "paragraph": { "setText": "$100", "textFormat": [{ "text": "$100", "bold": false }] } }] } },
      { "cell": { "row": 2, "col": 3 }, "textContent": { "paragraphOperations": [{ "operation": "insert", "index": 0, "paragraph": { "setText": "First item", "textFormat": [{ "text": "First item" }] } }] } }
    ]
  }
}

**Example: Add row with auto-resize:**
{
  "slideId": 1,
  "operationType": "edit",
  "shapeId": 123,
  "autoResize": true,
  "tableConfig": {
    "rowOperations": [{ "operation": "insert", "index": 3, "insertPosition": "after", "count": 1 }]
  }
}

**Example: Copy table style from template table:**
{
  "slideId": 2,
  "operationType": "edit",
  "shapeId": 200,
  "tableConfig": {
    "copyStyleFrom": {
      "sourceSlideId": 1,
      "sourceShapeId": 100,
      "copyWhat": ["all"],
      "detectPatterns": true,
      "adjustments": {
        "overrideColors": [{ "from": "#FF0000", "to": "#0000FF" }]
      }
    }
  }
}`,
            CreateOrEditTableParamsSchema
        );
    }

    public async execute(parameters: unknown): Promise<CreateOrEditTableResult> {
        const parseResult = safeParseToolParams(CreateOrEditTableParamsSchema, parameters);
        if (!parseResult.success) {
            throw new Error(`Invalid parameters: ${parseResult.error}`);
        }

        const params = parseResult.data;
        
        console.log(`[CreateOrEditTableTool] ${params.operationType === 'create' ? 'Creating' : 'Editing'} table on slide ${params.slideId}`);
        if (params.operationType === 'create') {
            console.log(`  Dimensions: ${params.tableConfig.rows}x${params.tableConfig.columns}`);
        } else {
            console.log(`  Shape ID: ${params.shapeId}`);
        }

        // Always use COM backend - Office.js PowerPoint API doesn't support row/column insertion
        // COM API has full table manipulation: table.Rows.Add(), table.Columns.Add(), etc.
        // Auto-resize is handled by Python COM with real table dimensions from PowerPoint
        console.log(`[CreateOrEditTableTool] Using COM backend for table operations...`);
        const comResult = await this.executeWithCOM(params);
        return await this.finalizeResult(params, comResult);
    }

    private async finalizeResult(
        params: CreateOrEditTableParams,
        result: CreateOrEditTableResult
    ): Promise<CreateOrEditTableResult> {
        if (!result.success) {
            return result;
        }

        const shapeId = String(result.shapeId ?? params.shapeId ?? "");
        if (shapeId) {
            await applyShapeMetadata({
                slideId: params.slideId,
                shapeId,
                metadata: params.metadata,
            });
        }

        return result;
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // OFFICE.JS IMPLEMENTATION (PRIMARY)
    // ═══════════════════════════════════════════════════════════════════════════

    /**
     * Execute table creation/editing using Office.js PowerPoint API
     */
    private async executeWithOfficeJS(params: CreateOrEditTableParams): Promise<CreateOrEditTableResult> {
        const warnings: string[] = [];
        const { tableConfig } = params;
        
        console.log(`[CreateOrEditTableTool:OfficeJS] Starting ${params.operationType} operation...`);
        
        return await PowerPoint.run(async (context) => {
            // Get the target slide (1-based index to 0-based)
            console.log(`[CreateOrEditTableTool:OfficeJS] Getting slide at index ${params.slideId - 1} (1-based: ${params.slideId})`);
            const slide = context.presentation.slides.getItemAt(params.slideId - 1);
            slide.load('id');
            await context.sync();
            console.log(`[CreateOrEditTableTool:OfficeJS] Got slide with Office.js ID: ${slide.id}`);
            
            let table: ExtendedTable | undefined;
            let shape: PowerPoint.Shape;
            let shapeId: number = 0;
            let finalRows: number = tableConfig.rows ?? 0;
            let finalColumns: number = tableConfig.columns ?? 0;
            
            if (params.operationType === "create") {
                // CREATE: Add a new table
                const rows = tableConfig.rows ?? 3;
                const columns = tableConfig.columns ?? 3;
                const left = params.position?.left ?? 100;
                const top = params.position?.top ?? 100;
                const width = params.position?.width ?? 400;
                const height = params.position?.height ?? 200;
                
                console.log(`[CreateOrEditTableTool:OfficeJS] Creating ${rows}x${columns} table at (${left}, ${top}, ${width}x${height})...`);
                
                shape = slide.shapes.addTable(rows, columns, {
                    left,
                    top,
                    width,
                    height,
                });
                
                shape.load(['id', 'left', 'top', 'width', 'height']);
                await context.sync();
                
                // We know the dimensions since we just created the table
                finalRows = rows;
                finalColumns = columns;
                
                // Try to get table reference for cell operations (may not be supported in all Office.js versions)
                try {
                    const tableRef = getTableFromShape(shape);
                    if (tableRef) {
                        table = tableRef;
                        console.log(`[CreateOrEditTableTool:OfficeJS] Got table reference for cell operations`);
                    }
                } catch (tableAccessError) {
                    console.warn(`[CreateOrEditTableTool:OfficeJS] Could not access table object: ${tableAccessError}`);
                    warnings.push('Table created but cell manipulation may be limited in Office.js');
                }
                
                console.log(`[CreateOrEditTableTool:OfficeJS] Table created with ID: ${shape.id}`);
            } else {
                // EDIT: Get existing table shape by ID
                console.log(`[CreateOrEditTableTool:OfficeJS] Looking for table shape with ID: ${params.shapeId}`);
                const shapes = slide.shapes;
                shapes.load('items');
                await context.sync();
                
                // Load all shape IDs to find our target
                for (const s of shapes.items) {
                    s.load('id,name,type');
                }
                await context.sync();
                
                const shapeIdList = shapes.items.map(s => `${s.id} (${s.name || 'unnamed'}, type=${s.type})`).join(', ');
                console.log(`[CreateOrEditTableTool:OfficeJS] Available shapes: [${shapeIdList}]`);
                
                const foundShape = shapes.items.find(s => {
                    const sId = s.id;
                    return sId === String(params.shapeId) || 
                           sId === params.shapeId?.toString() ||
                           parseInt(sId, 10) === params.shapeId;
                });
                
                if (!foundShape) {
                    throw new Error(`Table shape with ID ${params.shapeId} not found on slide ${params.slideId}. Available: [${shapeIdList}]`);
                }
                
                console.log(`[CreateOrEditTableTool:OfficeJS] Found shape: ID=${foundShape.id}, type=${foundShape.type}`);
                shape = foundShape;
                
                // Get the table from the shape (using type assertion for Office.js limitations)
                try {
                    const tableRef = getTableFromShape(shape);
                    if (tableRef) {
                        table = tableRef;
                        table.load('rowCount,columnCount');
                        await context.sync();
                        
                        finalRows = table.rowCount;
                        finalColumns = table.columnCount;
                        console.log(`[CreateOrEditTableTool:OfficeJS] Table dimensions: ${finalRows}x${finalColumns}`);
                    } else {
                        throw new Error('Table reference is undefined');
                    }
                } catch (tableAccessError) {
                    console.warn(`[CreateOrEditTableTool:OfficeJS] Could not access table for editing: ${tableAccessError}`);
                    warnings.push(`Could not access table internals via Office.js: ${tableAccessError}`);
                    // For edit mode, we might not be able to do much without table access
                    // Position updates will still work on the shape
                }
                
                // Update position if provided
                if (params.position) {
                    console.log(`[CreateOrEditTableTool:OfficeJS] Updating position...`);
                    if (params.position.left !== undefined) shape.left = params.position.left;
                    if (params.position.top !== undefined) shape.top = params.position.top;
                    if (params.position.width !== undefined) shape.width = params.position.width;
                    if (params.position.height !== undefined) shape.height = params.position.height;
                }
                
                // Process row operations (remove first, then insert) - requires table reference
                if (table && tableConfig.rowOperations && tableConfig.rowOperations.length > 0) {
                    console.log(`[CreateOrEditTableTool:OfficeJS] Processing ${tableConfig.rowOperations.length} row operations...`);
                    await this.processRowOperationsOfficeJS(table, tableConfig.rowOperations, context, warnings);
                    
                    // Reload row count after operations
                    table.load('rowCount');
                    await context.sync();
                    finalRows = table.rowCount;
                } else if (tableConfig.rowOperations && tableConfig.rowOperations.length > 0) {
                    warnings.push('Row operations skipped - table reference not available');
                }
                
                // Process column operations (remove first, then insert) - requires table reference
                if (table && tableConfig.columnOperations && tableConfig.columnOperations.length > 0) {
                    console.log(`[CreateOrEditTableTool:OfficeJS] Processing ${tableConfig.columnOperations.length} column operations...`);
                    await this.processColumnOperationsOfficeJS(table, tableConfig.columnOperations, context, warnings);
                    
                    // Reload column count after operations
                    table.load('columnCount');
                    await context.sync();
                    finalColumns = table.columnCount;
                } else if (tableConfig.columnOperations && tableConfig.columnOperations.length > 0) {
                    warnings.push('Column operations skipped - table reference not available');
                }
            }
            
            // Apply table operations only if we have a table reference
            if (table) {
                // Apply column widths if specified
                if (tableConfig.columnWidths && tableConfig.columnWidths.length > 0) {
                    console.log(`[CreateOrEditTableTool:OfficeJS] Applying column widths...`);
                    await this.applyColumnWidthsOfficeJS(table, tableConfig.columnWidths, context, warnings);
                }
                
                // Apply row heights if specified
                if (tableConfig.rowHeights && tableConfig.rowHeights.length > 0) {
                    console.log(`[CreateOrEditTableTool:OfficeJS] Applying row heights...`);
                    await this.applyRowHeightsOfficeJS(table, tableConfig.rowHeights, context, warnings);
                }
                
                // Apply cell text content
                if (tableConfig.cellText && tableConfig.cellText.length > 0) {
                    console.log(`[CreateOrEditTableTool:OfficeJS] Applying cell text for ${tableConfig.cellText.length} cells...`);
                    await this.applyCellTextOfficeJS(table, tableConfig.cellText, context, warnings);
                }
                
                // Apply cell formatting
                if (tableConfig.cellFormatting && tableConfig.cellFormatting.length > 0) {
                    console.log(`[CreateOrEditTableTool:OfficeJS] Applying cell formatting for ${tableConfig.cellFormatting.length} ranges...`);
                    await this.applyCellFormattingOfficeJS(table, tableConfig.cellFormatting, context, warnings);
                }
                
                // Handle cell merges
                if (tableConfig.mergeCells && tableConfig.mergeCells.length > 0) {
                    console.log(`[CreateOrEditTableTool:OfficeJS] Processing ${tableConfig.mergeCells.length} cell merges...`);
                    await this.processCellMergesOfficeJS(table, tableConfig.mergeCells, context, warnings);
                }
            } else {
                // Table reference not available - log what operations were skipped
                const skippedOps: string[] = [];
                if (tableConfig.columnWidths?.length) skippedOps.push('columnWidths');
                if (tableConfig.rowHeights?.length) skippedOps.push('rowHeights');
                if (tableConfig.cellText?.length) skippedOps.push('cellText');
                if (tableConfig.cellFormatting?.length) skippedOps.push('cellFormatting');
                if (tableConfig.mergeCells?.length) skippedOps.push('mergeCells');
                
                if (skippedOps.length > 0) {
                    const skipMsg = `Table created but these operations require COM fallback: ${skippedOps.join(', ')}`;
                    console.warn(`[CreateOrEditTableTool:OfficeJS] ${skipMsg}`);
                    warnings.push(skipMsg);
                }
            }
            
            // Load final shape properties
            shape.load(['id', 'left', 'top', 'width', 'height']);
            await context.sync();
            
            shapeId = parseInt(shape.id, 10) || 0;
            console.log(`[CreateOrEditTableTool:OfficeJS] SUCCESS! Table ID: ${shapeId}, Final dimensions: ${finalRows}x${finalColumns}`);
            
            return {
                success: true,
                slideId: params.slideId,
                shapeId,
                operationType: params.operationType,
                dimensions: {
                    rows: finalRows,
                    columns: finalColumns,
                },
                position: {
                    left: shape.left,
                    top: shape.top,
                    width: shape.width,
                    height: shape.height,
                },
                warnings,
                errors: [],
                executionMethod: "officejs" as const,
            };
        });
    }

    /**
     * Process row operations (remove first, then insert)
     */
    private async processRowOperationsOfficeJS(
        table: ExtendedTable,
        operations: RowOperation[],
        context: PowerPoint.RequestContext,
        warnings: string[]
    ): Promise<void> {
        // Separate remove and insert operations
        const removeOps = operations.filter(op => op.operation === 'remove').sort((a, b) => b.index - a.index);
        const insertOps = operations.filter(op => op.operation === 'insert').sort((a, b) => a.index - b.index);
        
        // Process removals first (in reverse order to maintain indices)
        for (const op of removeOps) {
            try {
                const rowIndex = op.index - 1;
                console.log(`[CreateOrEditTableTool:OfficeJS] Removing row at 1-based index ${op.index}`);
                table.deleteRow(rowIndex);
                await context.sync();
            } catch (err) {
                warnings.push(`Failed to remove row at index ${op.index}: ${err}`);
            }
        }
        
        // Process insertions (in order)
        for (const op of insertOps) {
            try {
                const count = op.count ?? 1;
                const anchorIndex = op.index - 1;
                const insertIndex = op.insertPosition === 'after' ? anchorIndex + 1 : anchorIndex;
                console.log(`[CreateOrEditTableTool:OfficeJS] Inserting ${count} row(s) ${op.insertPosition} row ${op.index}`);
                table.insertRow(insertIndex, count);
                await context.sync();
                
                if (op.spacingConsequences) {
                    console.log(`  Spacing consequences: ${op.spacingConsequences}`);
                }
            } catch (err) {
                warnings.push(`Failed to insert row at index ${op.index}: ${err}`);
            }
        }
    }

    /**
     * Process column operations (remove first, then insert)
     */
    private async processColumnOperationsOfficeJS(
        table: ExtendedTable,
        operations: ColumnOperation[],
        context: PowerPoint.RequestContext,
        warnings: string[]
    ): Promise<void> {
        // Separate remove and insert operations
        const removeOps = operations.filter(op => op.operation === 'remove').sort((a, b) => b.index - a.index);
        const insertOps = operations.filter(op => op.operation === 'insert').sort((a, b) => a.index - b.index);
        
        // Process removals first (in reverse order to maintain indices)
        for (const op of removeOps) {
            try {
                const colIndex = op.index - 1;
                console.log(`[CreateOrEditTableTool:OfficeJS] Removing column at 1-based index ${op.index}`);
                table.deleteColumn(colIndex);
                await context.sync();
            } catch (err) {
                warnings.push(`Failed to remove column at index ${op.index}: ${err}`);
            }
        }
        
        // Process insertions (in order)
        for (const op of insertOps) {
            try {
                const count = op.count ?? 1;
                const anchorIndex = op.index - 1;
                const insertIndex = op.insertPosition === 'after' ? anchorIndex + 1 : anchorIndex;
                console.log(`[CreateOrEditTableTool:OfficeJS] Inserting ${count} column(s) ${op.insertPosition} column ${op.index}`);
                table.insertColumn(insertIndex, count);
                await context.sync();
                
                if (op.spacingConsequences) {
                    console.log(`  Spacing consequences: ${op.spacingConsequences}`);
                }
            } catch (err) {
                warnings.push(`Failed to insert column at index ${op.index}: ${err}`);
            }
        }
    }

    /**
     * Apply column widths using table.columns collection
     * Note: Column width manipulation may have limited support in Office.js
     */
    private async applyColumnWidthsOfficeJS(
        table: ExtendedTable,
        columnWidths: number[],
        context: PowerPoint.RequestContext,
        warnings: string[]
    ): Promise<void> {
        try {
            // Try using the columns collection (PowerPointApi 1.9)
            const columns = table.columns;
            columns.load('items');
            await context.sync();
            
            for (let i = 0; i < Math.min(columnWidths.length, columns.items.length); i++) {
                try {
                    const column = columns.items[i];
                    (column as { width: number }).width = columnWidths[i];
                } catch (err) {
                    warnings.push(`Failed to set width for column ${i}: ${err}`);
                }
            }
            await context.sync();
        } catch (err) {
            warnings.push(`Column width adjustment not supported in this Office.js version: ${err}`);
        }
    }

    /**
     * Apply row heights using table.rows collection
     * Note: Row height manipulation may have limited support in Office.js
     */
    private async applyRowHeightsOfficeJS(
        table: ExtendedTable,
        rowHeights: number[],
        context: PowerPoint.RequestContext,
        warnings: string[]
    ): Promise<void> {
        try {
            // Try using the rows collection (PowerPointApi 1.9)
            const rows = table.rows;
            rows.load('items');
            await context.sync();
            
            for (let i = 0; i < Math.min(rowHeights.length, rows.items.length); i++) {
                try {
                    const row = rows.items[i];
                    (row as { height: number }).height = rowHeights[i];
                } catch (err) {
                    warnings.push(`Failed to set height for row ${i}: ${err}`);
                }
            }
            await context.sync();
        } catch (err) {
            warnings.push(`Row height adjustment not supported in this Office.js version: ${err}`);
        }
    }

    /**
     * Apply cell text content with paragraph operations
     * Uses getCellOrNullObject and cell.text per PowerPointApi 1.8
     * See: https://learn.microsoft.com/en-us/javascript/api/powerpoint/powerpoint.table
     */
    private async applyCellTextOfficeJS(
        table: ExtendedTable,
        cellTextEntries: CellTextWithOperations[],
        context: PowerPoint.RequestContext,
        warnings: string[]
    ): Promise<void> {
        for (const entry of cellTextEntries) {
            const row = entry.cell.row - 1;
            const col = entry.cell.col - 1;
            
            try {
                console.log(`[CreateOrEditTableTool:OfficeJS] Setting text for cell (${entry.cell.row}, ${entry.cell.col})`);
                
                // Use getCellOrNullObject as per the official API
                const cell = table.getCellOrNullObject(row, col);
                await context.sync();
                
                // Check if cell is valid (not part of a merged area that's not the top-left)
                if (cell.isNullObject) {
                    warnings.push(`Cell (${row}, ${col}) is part of a merged area or invalid`);
                    continue;
                }

                // Default autofit to shrink text on overflow unless explicitly set
                try {
                    const cellTextFrame = (cell as unknown as {
                        textFrame?: { autoSize?: PowerPoint.ShapeAutoSize };
                    }).textFrame;
                    if (cellTextFrame) {
                        const autofit = entry.textFrame?.autofit ?? "shrink_on_overflow";
                        const autofitMap = {
                            "none": PowerPoint.ShapeAutoSize.autoSizeNone,
                            "shrink_on_overflow": PowerPoint.ShapeAutoSize.autoSizeTextToFitShape,
                            "resize_shape": PowerPoint.ShapeAutoSize.autoSizeShapeToFitText,
                        };
                        if (autofit in autofitMap) {
                            cellTextFrame.autoSize = autofitMap[autofit as keyof typeof autofitMap];
                        }
                    }
                } catch (autofitError) {
                    warnings.push(`Could not set cell autofit for (${row}, ${col}): ${autofitError}`);
                }
                
                // Process paragraph operations - use cell.text directly per API 1.8
                if (entry.textContent?.paragraphOperations) {
                    for (const op of entry.textContent.paragraphOperations) {
                        if ((op.operation === 'insert' || op.operation === 'edit') && op.paragraph?.setText) {
                            // Set text directly using cell.text property (PowerPointApi 1.8)
                            cell.text = op.paragraph.setText;
                            console.log(`[CreateOrEditTableTool:OfficeJS] Set cell (${row}, ${col}) text to: "${op.paragraph.setText.substring(0, 30)}..."`);
                        } else if (op.operation === 'edit' && op.paragraph?.replaceText) {
                            // Text replacement - need to read current, replace, write back
                            warnings.push(`Text replace in cell (${row}, ${col}) requires reading current value first`);
                        }
                    }
                }
                
                await context.sync();
            } catch (err) {
                warnings.push(`Failed to set text for cell (${row}, ${col}): ${err}`);
            }
        }
    }

    /**
     * Apply cell formatting (fill, borders)
     * Uses getCellOrNullObject per PowerPointApi 1.8
     */
    private async applyCellFormattingOfficeJS(
        table: ExtendedTable,
        formattingEntries: CreateOrEditTableParams['tableConfig']['cellFormatting'],
        context: PowerPoint.RequestContext,
        warnings: string[]
    ): Promise<void> {
        if (!formattingEntries) return;
        
        for (const entry of formattingEntries) {
            const startRow = entry.cellRange.startRow - 1;
            const startCol = entry.cellRange.startCol - 1;
            const endRow = entry.cellRange.endRow - 1;
            const endCol = entry.cellRange.endCol - 1;
            
            // Apply to each cell in range
            for (let row = startRow; row <= endRow; row++) {
                for (let col = startCol; col <= endCol; col++) {
                    try {
                        const cell = table.getCellOrNullObject(row, col);
                        await context.sync();
                        
                        if (cell.isNullObject) {
                            continue; // Skip invalid/merged cells
                        }
                        
                        // Apply explicit formatting
                        if (entry.explicitFormatting) {
                            const fmt = entry.explicitFormatting;
                            
                            // Apply fill - cast to access fill property
                            if (fmt.fill) {
                                const cellWithFill = cell as ExtendedTableCell;
                                if (fmt.fill.null) {
                                    cellWithFill.fill.clear();
                                } else if (fmt.fill.color) {
                                    cellWithFill.fill.setSolidColor(fmt.fill.color);
                                }
                                if (fmt.fill.transparency !== undefined) {
                                    cellWithFill.fill.transparency = fmt.fill.transparency;
                                }
                            }
                            
                            // Note: Cell borders in Office.js PowerPoint API have limited support
                            if (fmt.borderTop || fmt.borderBottom || fmt.borderLeft || fmt.borderRight) {
                                warnings.push(`Cell borders for (${row}, ${col}) may have limited Office.js support`);
                            }
                        }
                        
                        await context.sync();
                    } catch (err) {
                        warnings.push(`Failed to format cell (${row}, ${col}): ${err}`);
                    }
                }
            }
        }
    }

    /**
     * Process cell merges
     */
    /**
     * Process cell merges
     * API signature: mergeCells(rowIndex, columnIndex, rowCount, columnCount)
     * See: https://learn.microsoft.com/en-us/javascript/api/powerpoint/powerpoint.table
     */
    private async processCellMergesOfficeJS(
        table: ExtendedTable,
        mergeRanges: CreateOrEditTableParams['tableConfig']['mergeCells'],
        context: PowerPoint.RequestContext,
        warnings: string[]
    ): Promise<void> {
        if (!mergeRanges) return;
        
        for (const range of mergeRanges) {
            try {
                // Convert from (startRow, startCol, endRow, endCol) to (rowIndex, colIndex, rowCount, colCount)
                const rowIndex = range.startRow - 1;
                const columnIndex = range.startCol - 1;
                const rowCount = range.endRow - range.startRow + 1;
                const columnCount = range.endCol - range.startCol + 1;
                
                console.log(`[CreateOrEditTableTool:OfficeJS] Merging cells at (${range.startRow},${range.startCol}) spanning ${rowCount}x${columnCount}`);
                
                // Office.js table API: mergeCells(rowIndex, columnIndex, rowCount, columnCount)
                table.mergeCells(rowIndex, columnIndex, rowCount, columnCount);
                await context.sync();
            } catch (err) {
                warnings.push(`Failed to merge cells (${range.startRow},${range.startCol})-(${range.endRow},${range.endCol}): ${err}`);
            }
        }
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // COM IMPLEMENTATION (FALLBACK)
    // ═══════════════════════════════════════════════════════════════════════════

    /**
     * Execute table creation/editing using Python COM backend (fallback)
     */
    private async executeWithCOM(params: CreateOrEditTableParams): Promise<CreateOrEditTableResult> {
        const warnings: string[] = [];
        const errors: string[] = [];
        const { tableConfig } = params;
        
        // Get deck ID for COM connection
        const documentId = await getOrCreatedocumentId();
        console.log(`[CreateOrEditTableTool] COM fallback using documentId: ${documentId}`);

        // Build request payload for Python COM backend
        // For edit operations: only send position if explicitly provided (preserve original dimensions)
        // For create operations: use defaults if not specified
        const comPayload = {
            documentId,
            slideId: params.slideId,
            operationType: params.operationType,
            shapeId: params.shapeId,
            position: params.position ? {
                left: params.position.left,
                top: params.position.top,
                width: params.position.width,
                height: params.position.height,
            } : (params.operationType === 'create' ? { left: 100, top: 100, width: 400, height: 200 } : undefined),
            rotation: params.rotation ?? 0,
            autoResize: params.autoResize ?? true,  // Default to true for auto-resize behavior
            tableConfig: {
                rows: tableConfig.rows,
                columns: tableConfig.columns,
                rowOperations: tableConfig.rowOperations,
                columnOperations: tableConfig.columnOperations,
                columnWidths: tableConfig.columnWidths,
                rowHeights: tableConfig.rowHeights,
                mergeCells: tableConfig.mergeCells,
                cellText: tableConfig.cellText?.map(ct => {
                    const formatting = this.extractCellTextFormatting(ct);
                    return {
                        cell: ct.cell,
                        text: this.extractCellText(ct),
                        textFrame: ct.textFrame,
                        // Text formatting properties
                        textColor: formatting?.textColor,
                        bold: formatting?.bold,
                        italic: formatting?.italic,
                        underline: formatting?.underline,
                        fontSize: formatting?.fontSize,
                        fontFamily: formatting?.fontFamily,
                        alignment: formatting?.alignment,
                    };
                }),
                // Transform cellFormatting to flatten explicitFormatting into the format Python expects
                cellFormatting: tableConfig.cellFormatting?.map(cf => ({
                    cellRange: cf.cellRange,
                    // Copy format from another table cell
                    copyFormatFrom: cf.copyFormatFrom ? {
                        componentId: cf.copyFormatFrom.componentId,
                        slideId: cf.copyFormatFrom.slideId,
                        tableShapeId: cf.copyFormatFrom.tableShapeId,
                        sourceCell: cf.copyFormatFrom.sourceCell,
                        sourceCellRange: cf.copyFormatFrom.sourceCellRange,
                        properties: cf.copyFormatFrom.properties,
                    } : undefined,
                    // Fill properties (explicit formatting)
                    fillColor: cf.explicitFormatting?.fill?.color,
                    fillTransparency: cf.explicitFormatting?.fill?.transparency,
                    clearFill: cf.explicitFormatting?.fill?.null,  // true = clear the fill
                    // Border properties (each has color, weight, dashStyle, transparency, null)
                    borderTop: cf.explicitFormatting?.borderTop,
                    borderBottom: cf.explicitFormatting?.borderBottom,
                    borderLeft: cf.explicitFormatting?.borderLeft,
                    borderRight: cf.explicitFormatting?.borderRight,
                })),
            },
        };

        console.log(`[CreateOrEditTableTool] Calling COM backend:`, comPayload);

        // Call Python COM backend
        const response = await fetch(`${BACKEND_BASE_URL}/com/createTable`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(comPayload),
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`COM backend error (${response.status}): ${errorText}`);
        }

        const comResult = await response.json();
        console.log(`[CreateOrEditTableTool] COM backend response:`, comResult);

        if (!comResult.success) {
            errors.push(comResult.message || 'COM operation failed');
        }

        // Get the final shape ID for potential style copy
        const finalShapeId = comResult.shapeId ?? params.shapeId ?? 0;

        // If copyStyleFrom is specified, apply table styling from source table
        if (comResult.success && tableConfig.copyStyleFrom && finalShapeId) {
            console.log(`[CreateOrEditTableTool] Applying copyStyleFrom from slide ${tableConfig.copyStyleFrom.sourceSlideId}, shape ${tableConfig.copyStyleFrom.sourceShapeId}`);
            
            // Get target table bounds to preserve during style copy
            // This ensures source dimensions are scaled to fit target, not vice versa
            const targetBounds = comResult.position && comResult.rows && comResult.columns ? {
                width: comResult.position.width,
                height: comResult.position.height,
                rowCount: comResult.rows,
                columnCount: comResult.columns,
                borderWeight: 1.0, // Default border weight for margin calculation
            } : undefined;
            
            if (targetBounds) {
                console.log(`[CreateOrEditTableTool] Target bounds for style copy: ${targetBounds.width}x${targetBounds.height}pt, ${targetBounds.rowCount}x${targetBounds.columnCount} cells`);
            } else {
                console.warn(`[CreateOrEditTableTool] No target bounds available - style copy may change table size`);
            }
            
            const styleResult = await this.applyCopyStyleFrom(
                documentId,
                tableConfig.copyStyleFrom,
                params.slideId,
                finalShapeId,
                warnings,
                targetBounds
            );
            
            if (!styleResult.success) {
                warnings.push(`Style copy warning: ${styleResult.error || 'Unknown error'}`);
            }
            
            // Log text scale factor if dimensions were shrunk
            if (styleResult.textScaleFactor && styleResult.textScaleFactor < 1.0) {
                console.log(`[CreateOrEditTableTool] Text scaled by ${(styleResult.textScaleFactor * 100).toFixed(1)}% to fit dimensions`);
            }
        }

        return {
            success: comResult.success,
            slideId: params.slideId,
            shapeId: finalShapeId,
            operationType: params.operationType,
            dimensions: {
                rows: comResult.rows ?? tableConfig.rows ?? 0,
                columns: comResult.columns ?? tableConfig.columns ?? 0,
            },
            position: comResult.position ?? (params.position ? {
                left: params.position.left ?? 0,
                top: params.position.top ?? 0,
                width: params.position.width ?? 0,
                height: params.position.height ?? 0,
            } : undefined),
            warnings: [...warnings, ...(comResult.warnings || [])],
            errors: [...errors, ...(comResult.errors || [])],
            executionMethod: "com" as const,
        };
    }

    /**
     * Apply copyStyleFrom by calling the copyTableStyle backend endpoint.
     * This copies styling from a source table to the target table.
     * 
     * IMPORTANT: Uses preserveTargetSize=true by default to ensure the target
     * table maintains its original dimensions. Source table dimensions are
     * scaled proportionally to fit the target bounds.
     */
    private async applyCopyStyleFrom(
        documentId: string,
        copyStyleFrom: CopyStyleFrom,
        targetSlideId: number,
        targetShapeId: number,
        warnings: string[],
        targetBounds?: {
            width: number;
            height: number;
            rowCount: number;
            columnCount: number;
            borderWeight?: number;
        }
    ): Promise<{ success: boolean; error?: string; textScaleFactor?: number }> {
        try {
            const response = await fetch(`${BACKEND_BASE_URL}/com/copyTableStyle`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    documentId,
                    slideId: copyStyleFrom.sourceSlideId,
                    sourceSlideId: copyStyleFrom.sourceSlideId,
                    sourceShapeId: copyStyleFrom.sourceShapeId,
                    targetSlideId,
                    targetShapeId,
                    copyWhat: copyStyleFrom.copyWhat ?? ['all'],
                    detectPatterns: copyStyleFrom.detectPatterns ?? true,
                    // NEW: Preserve target size - source dimensions are scaled to fit target
                    preserveTargetSize: true,
                    autoScaleText: true,
                    targetBounds: targetBounds ? {
                        width: targetBounds.width,
                        height: targetBounds.height,
                        rowCount: targetBounds.rowCount,
                        columnCount: targetBounds.columnCount,
                        borderWeight: targetBounds.borderWeight ?? 1.0,
                    } : undefined,
                    adjustments: copyStyleFrom.adjustments ? {
                        scaleWidth: copyStyleFrom.adjustments.scaleWidth,
                        scaleHeight: copyStyleFrom.adjustments.scaleHeight,
                        overrideColors: copyStyleFrom.adjustments.overrideColors,
                        overrideFont: copyStyleFrom.adjustments.overrideFont,
                        columnWidths: copyStyleFrom.adjustments.columnWidths,
                        rowHeights: copyStyleFrom.adjustments.rowHeights,
                    } : undefined,
                }),
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error('[CreateOrEditTableTool] copyTableStyle backend error:', errorText);
                return { success: false, error: `Backend error (${response.status}): ${errorText}` };
            }

            const data = await response.json();
            console.log('[CreateOrEditTableTool] copyTableStyle response:', {
                success: data.success,
                applied: data.applied,
                warningsCount: data.warnings?.length || 0,
                textScaleFactor: data.textScaleFactor,
                dimensionFitting: data.dimensionFitting ? {
                    scaleWidth: data.dimensionFitting.scale_width,
                    scaleHeight: data.dimensionFitting.scale_height,
                    fitsTarget: data.dimensionFitting.fits_target,
                } : null,
            });

            if (data.warnings && data.warnings.length > 0) {
                warnings.push(...data.warnings);
            }

            return { 
                success: data.success, 
                error: data.message,
                textScaleFactor: data.textScaleFactor,
            };
        } catch (error) {
            console.error('[CreateOrEditTableTool] copyStyleFrom error:', error);
            return { success: false, error: error instanceof Error ? error.message : String(error) };
        }
    }

    /**
     * Extract text from cell text entry for COM payload
     */
    private extractCellText(entry: CellTextWithOperations): string | undefined {
        if (!entry.textContent?.paragraphOperations) {
            return undefined;
        }

        const textParts: string[] = [];
        for (const op of entry.textContent.paragraphOperations) {
            // Handle both 'insert' and 'edit' operations - both can have setText
            if ((op.operation === 'insert' || op.operation === 'edit') && op.paragraph?.setText) {
                textParts.push(op.paragraph.setText);
            }
        }

        return textParts.length > 0 ? textParts.join('\n') : undefined;
    }

    /**
     * Extract text formatting from cell text entry for COM payload.
     * Uses the first textFormat run (COM supports single-format cell text).
     */
    private extractCellTextFormatting(entry: CellTextWithOperations): {
        textColor?: string;
        bold?: boolean;
        italic?: boolean;
        underline?: boolean;
        fontSize?: number;
        fontFamily?: string;
        alignment?: string;
    } | undefined {
        if (!entry.textContent?.paragraphOperations) {
            return undefined;
        }

        let alignment: string | undefined;
        let runFormatting: {
            textColor?: string;
            bold?: boolean;
            italic?: boolean;
            underline?: boolean;
            fontSize?: number;
            fontFamily?: string;
        } | undefined;

        for (const op of entry.textContent.paragraphOperations) {
            if ((op.operation === 'insert' || op.operation === 'edit') && op.paragraph) {
                if (!runFormatting && op.paragraph.textFormat?.length) {
                    const firstRun = op.paragraph.textFormat[0];
                    runFormatting = {
                        textColor: firstRun.textColor,
                        bold: firstRun.bold,
                        italic: firstRun.italic,
                        underline: firstRun.underline,
                        fontSize: firstRun.fontSize,
                        fontFamily: firstRun.fontFamily,
                    };
                }
                alignment = alignment ?? op.paragraph.paragraphAlignment;
            }
        }

        // Only return if we have at least one formatting property
        if (runFormatting || alignment !== undefined) {
            return {
                textColor: runFormatting?.textColor,
                bold: runFormatting?.bold,
                italic: runFormatting?.italic,
                underline: runFormatting?.underline,
                fontSize: runFormatting?.fontSize,
                fontFamily: runFormatting?.fontFamily,
                alignment,
            };
        }
        return undefined;
    }
}
